import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Row } from '@tanstack/react-table';
import { formatDate } from '../../common/utils';
import { filterString } from '../../common/utils/filters';
import { AlgorithmAlternative } from '../alternatives/types';
import { AlternativeAlgorithmSetDataType } from '../schema';
import { AlgorithmTargetRuleType } from '../types';
import {
  AlgorithmJoinFormDataType,
  AlgorithmJoinItemFormDataType,
} from './joins/schema/types';
import type { AlgorithmMappingQueryDtoType } from './mappings/schema/types';
import { getAlgorithmMappingLabel } from './mappings/utils';
import { AlgorithmExtendedType } from './schema/types';
import {
  AlgorithmSourceDimensionFormDataType,
  AlgorithmSourceFormDataType,
  AlgorithmSourcesConfidentialityRuleFormDataType,
} from './sources/schema/types';
import {
  AlgorithmTargetItemFormDataType,
  AlgorithmTargetRuleFormDataType,
} from './targets/schema/types';
import translations from './utils.translations.json';

const { addTranslations, t } = i18nNsFactory('algorithms-utils');
addTranslations(translations);

export interface AlgorithmAlternativeMapping {
  algorithmMap: Map<string, AlgorithmAlternative>;
  groupMap: Map<string, AlgorithmAlternative>;
}

export enum CloneModalState {
  CLONE = 'CLONE',
  CLONE_FINISH = 'CLONE_FINISH',
}

export interface AlgorithmCloneProps {
  algorithmId: number;
  state: CloneModalState;
}

export const alternativeColumnFilterFn = (
  row: Row<AlgorithmExtendedType>,
  columnId: string,
  filter: any,
) => {
  if (!filter) {
    return true;
  }

  const alternative: Pick<AlternativeAlgorithmSetDataType, 'code' | 'name'> =
    row.renderValue(columnId);
  if (!alternative) {
    return false;
  }

  let found = filterString(alternative.code.toString(), filter);
  if (!found) {
    found = filterString(alternative.name, filter);
  }

  return found;
};

export const alternativeColumnSortingFn = (
  a: Row<AlgorithmExtendedType>,
  b: Row<AlgorithmExtendedType>,
  columnId: string,
) => {
  const alternativeA: Pick<AlternativeAlgorithmSetDataType, 'code'> =
    a.renderValue(columnId);
  const alternativeB: Pick<AlternativeAlgorithmSetDataType, 'code'> =
    b.renderValue(columnId);
  return (
    (alternativeA?.code ?? Number.MAX_SAFE_INTEGER) -
    (alternativeB?.code ?? Number.MAX_SAFE_INTEGER)
  );
};

export function findAlgorithmAlternative(
  code: string,
  groupCode: string,
  alternativeMapping: AlgorithmAlternativeMapping,
): AlgorithmAlternative | undefined {
  const algorithmMatch = alternativeMapping.algorithmMap.get(code);
  const groupMatch = groupCode
    ? alternativeMapping.groupMap.get(groupCode)
    : undefined;

  if (!algorithmMatch && !groupMatch) {
    return undefined;
  }

  return {
    ...(algorithmMatch ?? groupMatch),
    defaultAlgorithm:
      (algorithmMatch?.defaultAlgorithm || groupMatch?.defaultAlgorithm) ??
      false,
  } as AlgorithmAlternative;
}

export function initAlgorithmAlternatives(
  alternatives: AlternativeAlgorithmSetDataType[],
): AlgorithmAlternativeMapping {
  const algorithmMap = new Map<string, AlgorithmAlternative>();
  const groupMap = new Map<string, AlgorithmAlternative>();

  alternatives.forEach((alternativeSet) =>
    alternativeSet.alternativeAlgorithms.forEach((alternative) => {
      if (alternative.code) {
        algorithmMap.set(alternative.code, {
          code: alternativeSet.code,
          name: alternativeSet.name,
          defaultAlgorithm: alternative.defaultAlgorithm,
        });
      }
      if (alternative.groupCode) {
        groupMap.set(alternative.groupCode, {
          code: alternativeSet.code,
          name: alternativeSet.name,
          defaultAlgorithm: alternative.defaultAlgorithm,
        });
      }
    }),
  );

  return {
    algorithmMap,
    groupMap,
  };
}

export type AlgorithmSourceLabel =
  | `${string} | ${string}[T${number}] - ${string}`
  | `${string}[T${number}] - ${string}`
  | `${string} | ${string} - ${string}`
  | `${string} - ${string}`;

export type AlgorithmSource = Pick<
  AlgorithmSourceFormDataType,
  'sourceCode' | 'alias' | 'numberOfPreviousPeriod'
>;

export type AlgorithmMapping = Pick<AlgorithmMappingQueryDtoType, 'alias'>;

export const getConfidentialityRuleSourceLabel = (
  confidentialityRule: AlgorithmSourcesConfidentialityRuleFormDataType,
  sources: AlgorithmSource[],
): string => {
  if (sources.length === 0) return t('confidentialityPublic');

  const confidentialityRuleSource = confidentialityRule.source;
  if (!confidentialityRuleSource) return t('confidentialityPublic');

  const source = sources[confidentialityRuleSource.index];
  const sourceDimension = confidentialityRule.source.dimension;

  return `${getAlgorithmSourceShortLabel(source)}.${sourceDimension}`;
};

export const getAlgorithmSourceLabel = (
  algorithmSource: AlgorithmSource & { sourceName: string },
): AlgorithmSourceLabel => {
  const {
    sourceName,
    sourceCode,
    alias,
    numberOfPreviousPeriod = 0,
  } = algorithmSource;

  return `${alias ? alias + ' | ' : ''}${getAlgorithmSourceShortLabel({
    sourceCode,
    numberOfPreviousPeriod,
  })} - ${sourceName}`;
};

export const getAlgorithmSourceShortLabel = (
  algorithmSource?: AlgorithmSource,
): string => {
  if (!algorithmSource) {
    return '';
  }

  const { sourceCode, alias, numberOfPreviousPeriod = 0 } = algorithmSource;

  return (
    alias ||
    `${sourceCode}${
      numberOfPreviousPeriod && numberOfPreviousPeriod > 0
        ? `[T+${numberOfPreviousPeriod}]`
        : numberOfPreviousPeriod && numberOfPreviousPeriod < 0
          ? `[T${numberOfPreviousPeriod}]`
          : ''
    }`
  );
};

export const getAlgorithmJoinLabels = (
  joins: AlgorithmJoinFormDataType[],
  isDimensionWithDateDataType: (
    dimension?: AlgorithmSourceDimensionFormDataType,
  ) => boolean,
  sourcesById: Map<number, AlgorithmSourceFormDataType>,
) =>
  joins
    .slice(1)
    .map((join, index) =>
      getAlgorithmJoinLabel(
        join,
        isDimensionWithDateDataType,
        sourcesById,
        index === 0
          ? `${getSourceLabel(joins[0].algorithmSourceId, sourcesById)} `
          : '',
      ),
    );

const getAlgorithmJoinLabel = (
  join: AlgorithmJoinFormDataType,
  isDimensionWithDateDataType: (
    dimension?: AlgorithmSourceDimensionFormDataType,
  ) => boolean,
  sourcesById: Map<number, AlgorithmSourceFormDataType>,
  prefix?: string,
) => {
  const sourceLabel = getSourceLabel(join.algorithmSourceId, sourcesById);

  return `${prefix}${t(
    `join-type.${join.joinTypeCode}`,
  )} ${sourceLabel} (${join.algorithmJoinItems
    .map((item) => {
      const { sourceLabel, targetLabel } = getAlgorithmJoinItemLabels(
        sourcesById,
        isDimensionWithDateDataType,
        join,
        item,
      );

      return [sourceLabel, targetLabel].join(' = ');
    })
    .join(', ')})`;
};

const formatValue = (
  sourcesById: Map<number, AlgorithmSourceFormDataType>,
  isDimensionWithDateDataType: (
    dimension?: AlgorithmSourceDimensionFormDataType,
  ) => boolean,
  value: string,
  oppositeValue: string,
  oppositeAlgorithmSourceId?: number,
) => {
  if (!oppositeAlgorithmSourceId) {
    return value;
  }

  const oppositeSource = sourcesById.get(oppositeAlgorithmSourceId);
  const oppositeDimension = oppositeSource?.dimensions.find(
    ({ code }) => code === oppositeValue,
  );

  return oppositeDimension && isDimensionWithDateDataType(oppositeDimension)
    ? formatDate(value) || ''
    : value;
};

export const getAlgorithmJoinItemLabels = (
  sourcesById: Map<number, AlgorithmSourceFormDataType>,
  isDimensionWithDateDataType: (
    dimension?: AlgorithmSourceDimensionFormDataType,
  ) => boolean,
  join: AlgorithmJoinFormDataType,
  item: AlgorithmJoinItemFormDataType,
) => {
  const joinSourceLabel = getSourceLabel(join.algorithmSourceId, sourcesById);

  switch (item._variant) {
    case 'DIMENSION_DIMENSION':
      return {
        sourceLabel: `${((
          sourcesById: Map<number, AlgorithmSourceFormDataType>,
          sourceId: number,
        ) => getSourceLabel(sourceId, sourcesById))(
          sourcesById,
          item.algorithmSourceId,
        )}.${item.sourceDimension}`,
        targetLabel: `${joinSourceLabel}.${item.targetDimension}`,
      };
    case 'DIMENSION_VALUE':
      return {
        sourceLabel: `${((
          sourcesById: Map<number, AlgorithmSourceFormDataType>,
          sourceId: number,
        ) => getSourceLabel(sourceId, sourcesById))(
          sourcesById,
          item.algorithmSourceId,
        )}.${item.sourceDimension}`,
        targetLabel: formatValue(
          sourcesById,
          isDimensionWithDateDataType,
          item.targetValue,
          item.sourceDimension,
          item.algorithmSourceId,
        ),
      };
    case 'VALUE_DIMENSION':
      return {
        sourceLabel: formatValue(
          sourcesById,
          isDimensionWithDateDataType,
          item.sourceValue,
          item.targetDimension,
          join.algorithmSourceId,
        ),
        targetLabel: `${joinSourceLabel}.${item.targetDimension}`,
      };
    case 'MAPPING_DIMENSION':
      return {
        sourceLabel: item.algorithmMappingAlias,
        targetLabel: `${joinSourceLabel}.${item.targetDimension}`,
      };
    case 'MAPPING_VALUE':
      return {
        sourceLabel: item.algorithmMappingAlias,
        targetLabel: item.targetValue,
      };
    case 'DIMENSION_MAPPING':
      return {
        sourceLabel: `${((
          sourcesById: Map<number, AlgorithmSourceFormDataType>,
          sourceId: number,
        ) => getSourceLabel(sourceId, sourcesById))(
          sourcesById,
          item.algorithmSourceId,
        )}.${item.sourceDimension}`,
        targetLabel: item.algorithmMappingAlias,
      };
    case 'VALUE_MAPPING':
      return {
        sourceLabel: item.sourceValue,
        targetLabel: item.algorithmMappingAlias,
      };
  }
};

export const getAlgorithmTargetMappingRuleLabels = (
  rules: AlgorithmTargetRuleFormDataType[],
  sourcesById: Map<number, AlgorithmSource>,
) =>
  rules
    .filter((rule) => rule.ruleType === AlgorithmTargetRuleType.MAP)
    .flatMap((rule) =>
      rule.items.map((item) =>
        getAlgorithmTargetMappingRuleItemLabel(rule, item, sourcesById),
      ),
    );

export const getAlgorithmTargetMappingRuleItemLabel = (
  rule: AlgorithmTargetRuleFormDataType,
  item: AlgorithmTargetItemFormDataType,
  sourcesById: Map<number, AlgorithmSource>,
  prefix?: string,
): string => {
  const {
    ruleCode,
    algorithmSourceId,
    algorithmSource,
    algorithmMapping,
    sourceItemCode,
  } = rule;

  const resultCode =
    item.dimensionCode || item.indicatorCode || t('valueUnavailable');
  const source =
    algorithmSource ??
    (algorithmSourceId ? sourcesById.get(algorithmSourceId) : undefined);

  const sourceCode = source
    ? getAlgorithmSourceShortLabel(source)
    : algorithmMapping
      ? getAlgorithmMappingLabel(algorithmMapping, sourcesById)
      : '';

  const itemCodeSuffix = sourceCode
    ? sourceItemCode
      ? `.${sourceItemCode}`
      : ''
    : (sourceItemCode ?? '');

  const labelPrefix = prefix ?? ruleCode;

  return `${labelPrefix}: ${resultCode} = ${sourceCode}${itemCodeSuffix}`;
};

export const getAlgorithmTargetRuleRuleLabels = (
  rules: AlgorithmTargetRuleFormDataType[],
) =>
  rules
    .filter((rule) => rule.ruleType === AlgorithmTargetRuleType.RULE)
    .flatMap(
      (rule) =>
        rule.items?.map((item) =>
          getAlgorithmTargetRuleRuleItemLabel(rule, item),
        ) ?? [],
    );

export const getAlgorithmTargetRuleRuleItemLabel = (
  rule: AlgorithmTargetRuleFormDataType,
  item: AlgorithmTargetItemFormDataType,
  prefix?: string,
) => {
  const resultCode = item.dimensionId
    ? item.dimensionCode
    : item.confidentialityStatus
      ? t('ruleTypeTargetRules.conf', {
          code: item.indicatorCode,
        })
      : item.indicatorCode;
  return `${prefix ?? rule.ruleCode}: ${resultCode} = ${rule.expression}`;
};

const getSourceLabel = (
  sourceId: number,
  sourcesById: Map<number, AlgorithmSource>,
) => {
  const source = sourcesById.get(sourceId);
  return source ? getAlgorithmSourceShortLabel(source) : '';
};
